import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { EditorContent, useEditor } from "@tiptap/react";
import { Placeholder } from "@tiptap/extensions";
import TiptapEditorToolbar from "./TiptapEditorToolbar";
import { EDITOR_EXTENSIONS } from "./editorConfig";
import "./tiptapeditor.scss";

/**
 * An empty document serialises as `<p></p>`, but callers compare the editor's
 * value against a stored note to decide whether anything needs saving — so an
 * empty editor has to read back as the empty string, not as empty markup.
 */
const readHtml = (editor) => (editor.isEmpty ? "" : editor.getHTML());

const TiptapEditor = ({
  value,
  onChange,
  onBlur,
  placeholder,
  ariaLabel,
  editable
}) => {
  // Held in refs so a caller re-rendering with new inline callbacks — or a new
  // placeholder — never tears down and rebuilds the editor, which would cost
  // the operator their cursor and their undo history.
  const callbacksRef = useRef({ onChange, onBlur });
  const placeholderRef = useRef(placeholder);

  useEffect(() => {
    callbacksRef.current = { onChange, onBlur };
    placeholderRef.current = placeholder;
  }, [onChange, onBlur, placeholder]);

  const editor = useEditor({
    extensions: [
      ...EDITOR_EXTENSIONS,
      Placeholder.configure({ placeholder: () => placeholderRef.current })
    ],
    content: value,
    editorProps: {
      attributes: {
        class: "tiptap-editor__content",
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": ariaLabel
      }
    },
    onUpdate: ({ editor: instance }) =>
      callbacksRef.current.onChange?.(readHtml(instance)),
    onBlur: ({ editor: instance }) =>
      callbacksRef.current.onBlur?.(readHtml(instance))
  });

  useEffect(() => {
    if (editor) editor.setEditable(editable);
  }, [editor, editable]);

  // Adopt content that arrived from outside (the note loading, or a reset)
  // without disturbing an operator who is mid-sentence: an echo of what the
  // editor already holds is not a change.
  useEffect(() => {
    if (!editor) return;
    const incoming = value ?? "";
    if (incoming === readHtml(editor)) return;
    editor.commands.setContent(incoming, { emitUpdate: false });
  }, [editor, value]);

  if (!editor) return null;

  return (
    <div className="tiptap-editor">
      {editable && <TiptapEditorToolbar editor={editor} />}
      <EditorContent editor={editor} className="tiptap-editor__content-wrap" />
    </div>
  );
};

TiptapEditor.propTypes = {
  /** Editor content as an HTML string. */
  value: PropTypes.string,
  /** Fires on every keystroke with the current HTML ("" when empty). */
  onChange: PropTypes.func,
  /** Fires when focus leaves the editor — the hook to persist from. */
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
  editable: PropTypes.bool
};

TiptapEditor.defaultProps = {
  value: "",
  onChange: undefined,
  onBlur: undefined,
  placeholder: "",
  ariaLabel: "Rich text editor",
  editable: true
};

export default TiptapEditor;
